/**
 * Hook para escutar notificações via WebSocket
 *
 * Conecta eventos do WebSocket ao store de notificações,
 * permitindo que notificações em tempo real sejam exibidas.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useCallback } from 'react';
import { useSocketEvent } from './useWebSocket';
import { useNotificationStore } from '@store/useNotificationStore';

/**
 * Tipos de eventos WebSocket que geram notificações
 */
interface OrderCreatedEvent {
  type: 'ORDER_CREATED';
  data: {
    id: string;
    orderNumber: number;
    total: number;
    items: Array<{ productName: string; quantity: number }>;
  };
  timestamp: string;
}

interface OrderStatusEvent {
  type: 'ORDER_STATUS_UPDATED';
  data: {
    orderId: string;
    status: string;
    order: {
      id: string;
      orderNumber: number;
    };
  };
  timestamp: string;
}

interface NotificationEvent {
  type: 'USER_NOTIFICATION' | 'TENANT_NOTIFICATION';
  data: {
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
    data?: Record<string, unknown>;
  };
  timestamp: string;
}

/**
 * Mapeia tipo de notificação do backend para o frontend
 */
function mapNotificationType(type: string): 'order' | 'payment' | 'delivery' | 'system' | 'message' {
  switch (type) {
    case 'info':
      return 'system';
    case 'success':
      return 'order';
    case 'warning':
      return 'delivery';
    case 'error':
      return 'system';
    default:
      return 'system';
  }
}

/**
 * Hook que escuta eventos WebSocket e cria notificações
 *
 * Usa useSocketEvent para registrar listeners com o padrão de handler-ref,
 * evitando re-registros desnecessários quando os callbacks mudam.
 *
 * @example
 * // No componente principal (App ou AuthProvider)
 * useNotificationListener();
 */
export function useNotificationListener(): void {
  const { addNotification } = useNotificationStore();

  const handleOrderCreated = useCallback(
    (event: OrderCreatedEvent) => {
      addNotification({
        type: 'order',
        title: 'Novo Pedido!',
        message: `Pedido #${event.data.orderNumber} recebido - R$ ${event.data.total.toFixed(2)}`,
        link: `/orders?id=${event.data.id}`,
        data: event.data,
      });
    },
    [addNotification],
  );

  const handleOrderStatus = useCallback(
    (event: OrderStatusEvent) => {
      const statusMessages: Record<string, string> = {
        PENDING_PAYMENT: 'aguardando pagamento',
        CONFIRMED: 'confirmado',
        PREPARING: 'em preparo',
        READY: 'pronto para retirada',
        OUT_FOR_DELIVERY: 'saiu para entrega',
        DELIVERED: 'entregue',
        COMPLETED: 'finalizado',
        CANCELLED: 'cancelado',
      };

      const statusMessage = statusMessages[event.data.status] || event.data.status;

      addNotification({
        type: event.data.status === 'CANCELLED' ? 'system' : 'order',
        title: 'Status Atualizado',
        message: `Pedido #${event.data.order.orderNumber} ${statusMessage}`,
        link: `/orders?id=${event.data.orderId}`,
        data: event.data,
      });
    },
    [addNotification],
  );

  const handleNotification = useCallback(
    (event: NotificationEvent) => {
      addNotification({
        type: mapNotificationType(event.data.type),
        title: event.data.title,
        message: event.data.message,
        data: event.data.data,
      });
    },
    [addNotification],
  );

  useSocketEvent<OrderCreatedEvent>('order:created', handleOrderCreated);
  useSocketEvent<OrderStatusEvent>('order:status', handleOrderStatus);
  useSocketEvent<NotificationEvent>('notification', handleNotification);
}

export default useNotificationListener;
